import React from 'react';
import { Platform } from 'react-native';

/**
 * Public fallback Client ID. This is PUBLIC — Google OAuth Client IDs are
 * designed to be embedded in frontend bundles, and this one is already
 * committed in `.env.example`. Security comes from "Authorized JavaScript
 * origins" in Google Cloud Console, not from the ID being secret.
 *
 * Hardcoded because Expo SDK 49 + `expo export:web` + Vercel's
 * `@vercel/static-build` currently don't forward `EXPO_PUBLIC_*` env vars
 * into the webpack DefinePlugin, so `process.env.EXPO_PUBLIC_*` is
 * `undefined` at runtime in production bundles (the same is true for
 * `EXPO_PUBLIC_API_BASE_URL`, which is why `remembrApi.ts` also hardcodes
 * its base URL as a fallback). Keep this value in sync with `.env.example`
 * and the backend `GOOGLE_CLIENT_IDS` env var. If the Expo env-var pipeline
 * ever starts working, `process.env.…` will take precedence.
 */
const GOOGLE_CLIENT_ID_FALLBACK =
  '1069549778563-648ffqrda8vmoc5602n4ei2t8mf8bba8.apps.googleusercontent.com';

export function getGoogleClientId(): string | null {
  const fromEnv = process.env.EXPO_PUBLIC_GOOGLE_CLIENT_ID_WEB;
  return (fromEnv && fromEnv.length > 0 ? fromEnv : GOOGLE_CLIENT_ID_FALLBACK) || null;
}

/**
 * Web-only wrapper around `@react-oauth/google`'s `GoogleOAuthProvider`.
 *
 * On native this renders its children untouched — mobile sign-in will use
 * `expo-auth-session/providers/google` in a follow-up PR and does NOT need
 * this provider.
 *
 * We use a lazy `require` inside the web branch so Metro (the React Native
 * bundler) never tries to bundle `@react-oauth/google` for native builds.
 * The package pulls in browser-only APIs (`window.google`) that would crash
 * the iOS/Android bundle at import time otherwise.
 */
export function GoogleAuthProvider({ children }: { children: React.ReactNode }) {
  if (Platform.OS !== 'web') {
    return <>{children}</>;
  }

  const clientId = getGoogleClientId();
  if (!clientId) {
    // Only reachable if the fallback constant is also blanked out — should
    // never happen in practice. Log in dev so the button-inert case is
    // obvious locally.
    if (__DEV__) {
      // eslint-disable-next-line no-console
      console.warn(
        '[GoogleAuthProvider] No Google Client ID resolved (env + fallback empty). ' +
          'Google Sign-In will be inert.'
      );
    }
    return <>{children}</>;
  }

  // Lazy-required on purpose — see comment above.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { GoogleOAuthProvider } = require('@react-oauth/google');
  return <GoogleOAuthProvider clientId={clientId}>{children}</GoogleOAuthProvider>;
}

export default GoogleAuthProvider;
